/* eslint-disable no-param-reassign */
import { AddressBookRecord as FredAddressBookRecord } from '@tassat/fred-definitions';
import { AddressBookRecord as PaymentsAddressBookRecord } from '@tassat/payments-definitions';
import { ActionTypes } from '@tassat/ui-definitions';
import { Dispatch, SetStateAction } from 'react';

import { IErrors, IFormValues } from './types';
import {
    textFieldCharacterWhitelist,
    validateNoExcelFormulaStartingCharacters,
    validateWalletAddress
} from '../helpers/validationHelpers';

const validateNickNames = (values: IFormValues, errors: IErrors, nickName) => {
    const value = values[nickName].trim();
    const nicknameExcelError = validateNoExcelFormulaStartingCharacters(value);
    if (nicknameExcelError) errors[nickName] = nicknameExcelError;

    const nickNameWhitelistError = textFieldCharacterWhitelist(value);

    if (nickNameWhitelistError) errors[nickName] = nickNameWhitelistError;

    return errors;
};

const validateNickNameUniqueness = (nickname, addressBook, field, errors: IErrors) => {
    if (field === 'nickName1') {
        const nickNameExists = addressBook.find((contact) => contact.NickName === nickname);
        if (nickNameExists) {
            errors[field] = `Nickname '${nickname}' already assigned to a contact in address book`;
        }
    }

    if (field === 'nickName2') {
        const nickNameExists = addressBook.find((contact) => contact.NickName2 === nickname);
        if (nickNameExists) {
            errors[field] = `Nickname '${nickname}' already assigned to a contact in address book`;
        }
    }

    return errors;
};

export const validateAddCounterpartyForm = async (
    values: IFormValues,
    opts: {
        actionType: ActionTypes;
        addressBookContacts: FredAddressBookRecord[] | PaymentsAddressBookRecord[];
        setFirmWalletInfo: Dispatch<SetStateAction<{
            BankName?: string;
            walletInfo: boolean;
            FirmName: string;
            WalletName1: string;
            WalletName2?: string;
        }>>;
        initialValues: IFormValues;
        validateWalletExists: (walletAddress: string) => any;
    }
) => {
    const {
        Wallet,
        nickName1,
        nickName2,
        AssociatedAccountNumber
    } = values;
    const {
        actionType,
        addressBookContacts,
        setFirmWalletInfo,
        initialValues,
        validateWalletExists
    } = opts;
    let errors: IErrors = {} as IErrors;

    if (actionType === ActionTypes.add) {
        const walletAddressError = validateWalletAddress(
            Wallet,
            'Account'
        );
        const contactExists = addressBookContacts
            .find((contact) => ((contact as FredAddressBookRecord).Account === Wallet));

        if (walletAddressError) {
            errors.Wallet = walletAddressError;
        } else if (contactExists) {
            errors.Wallet = 'Contact already in address book';
        } else {
            const emptyInfo = {
                walletInfo: false,
                FirmName: '',
                WalletName1: '',
                WalletName2: '',
                AssociatedAccountNumber: ''
            };

            try {
                const walletInfo = await validateWalletExists(Wallet);
                if (walletInfo.error) {
                    errors.Wallet = walletInfo.message;
                    setFirmWalletInfo(emptyInfo);
                } else {
                    setFirmWalletInfo({
                        ...walletInfo,
                        walletInfo: true
                    });
                }
            } catch (error) {
                errors.Wallet = 'Something went wrong';
                setFirmWalletInfo(emptyInfo);
            }
        }

        if (errors.Wallet) {
            return errors;
        }
    }

    if (
        nickName1 && (
            actionType === ActionTypes.add ||
            (actionType === ActionTypes.edit && nickName1 !== initialValues.nickName1)
        )
    ) {
        errors = validateNickNames(values, errors, 'nickName1');
        errors = validateNickNameUniqueness(nickName1, addressBookContacts, 'nickName1', errors);
    }

    if (
        nickName2 && (
            actionType === ActionTypes.add ||
            (actionType === ActionTypes.edit && nickName2 !== initialValues.nickName2)
        )
    ) {
        errors = validateNickNames(values, errors, 'nickName2');
        errors = validateNickNameUniqueness(nickName2, addressBookContacts, 'nickName2', errors);
    }

    if (nickName2 && !nickName1) {
        errors.nickName2 = 'Cannot enter a second nickname without a first';
    }

    const associatedAccountNumber = AssociatedAccountNumber.trim();

    const associatedAccountNumberExcelError = validateNoExcelFormulaStartingCharacters(associatedAccountNumber);
    const associatedAccountNumberWhitelistingValidation = textFieldCharacterWhitelist(associatedAccountNumber);

    if (associatedAccountNumberExcelError) errors.AssociatedAccountNumber = associatedAccountNumberExcelError;
    else if (associatedAccountNumberWhitelistingValidation)
        errors.AssociatedAccountNumber = associatedAccountNumberWhitelistingValidation;

    return errors;
};